// src/shared/components/TeamSection.jsx
import * as React from "react";
import {
    Box,
    Typography,
    Paper,
    IconButton,
    Grid,
    Collapse,
} from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";

const CONTENT_MAX = 1720;

function TeamCard({ name, org, photoUrl, profileUrl, onToggle, isActive }) {
    return (
        <Box sx={{ width: "100%", maxWidth: 240 }}>
            <Paper
                variant="outlined"
                elevation={12}
                onClick={onToggle}
                sx={{
                    position: "relative",
                    borderRadius: 4,
                    overflow: "hidden",
                    bgcolor: "#20BC71",
                    width: "100%",
                    aspectRatio: "1 / 1",
                    cursor: "pointer",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    outline: isActive ? "3px solid #111" : "none",
                    "&:hover": {
                        transform: "translateY(-4px)",
                    },
                }}
            >
                {photoUrl ? (
                    <Box
                        component="img"
                        src={photoUrl}
                        alt={name}
                        sx={{
                            position: "absolute",
                            inset: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                        }}
                    />
                ) : null}

                {profileUrl ? (
                    <IconButton
                        component="a"
                        
                        rel="noopener noreferrer"
                        sx={{
                            position: "absolute",
                            top: 12,
                            right: 12,
                            width: "16%",
                            height: "16%",
                            bgcolor: "#FFF",
                            "&:hover": {
                                bgcolor: "#f4f4f4",
                            },
                        }}
                    >
                        <LaunchIcon sx={{ fontSize: "80%" }} />
                    </IconButton>
                ) : null}
            </Paper>

            <Box
                onClick={onToggle}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    cursor: "pointer",
                }}
            >
                <Typography variant="body1" sx={{ mt: 1.25, textAlign: "left" }}>
                    {name}
                </Typography>
                <Typography variant="body2" sx={{ color: "#555", textAlign: "left" }}>
                    {org}
                </Typography>
            </Box>
        </Box>
    );
}

export default function TeamSection({
    title = "Research team",
    members = [],
}) {
    const [openMemberId, setOpenMemberId] = React.useState(null);

    const activeMember = React.useMemo(() => {
        return members.find((m, index) => (m.id ?? m.name ?? index) === openMemberId) ?? null;
    }, [members, openMemberId]);

    const handleToggle = (memberId) => {
        setOpenMemberId((prev) => (prev === memberId ? null : memberId));
    };

    return (
        <Box
            sx={{
                width: "100%",
                mx: "auto",
                maxWidth: `${CONTENT_MAX}px`,
                borderRadius: 6,
                background: "#20BC71",
                p: 10,
            }}
        >
            <Box sx={{ width: "100%", mb: 6, textAlign: "left" }}>
                <Typography variant="h3" sx={{ lineHeight: 1.05 }}>
                    {title}
                </Typography>
            </Box>

            <Grid
                container
                columns={12}
                columnSpacing={4}
                rowSpacing={4}
                sx={{
                    width: "100%",
                    justifyContent: "space-between",
                }}
            >
                {members.map((m, index) => {
                    const memberKey = m.id ?? m.name ?? index;
                    const isActive = openMemberId === memberKey;

                    return (
                        <Grid
                            key={memberKey}
                            size={2}
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            <TeamCard
                                {...m}
                                isActive={isActive}
                                onToggle={() => handleToggle(memberKey)}
                            />
                        </Grid>
                    );
                })}
            </Grid>

            <Collapse in={Boolean(activeMember)} timeout="auto" unmountOnExit>
                <Box
                    sx={{
                        width: "100%",
                        mt: 5,
                        p: { xs: 2, md: 4 },
                        borderRadius: 4,
                        bgcolor: "rgba(255,255,255,0.72)",
                        border: "1px solid rgba(0,0,0,0.14)",
                    }}
                >
                    <Typography
                        variant="h5"
                        sx={{
                            mb: 1,
                            textAlign: "left",
                        }}
                    >
                        {activeMember?.name}
                    </Typography>

                    {activeMember?.org ? (
                        <Typography
                            variant="body2"
                            sx={{
                                mb: 2,
                                textAlign: "left",
                                color: "#444",
                            }}
                        >
                            {activeMember.org}
                        </Typography>
                    ) : null}

                    <Typography
                        variant="body1"
                        sx={{
                            textAlign: "left",
                            color: "#222",
                            whiteSpace: "pre-line",
                            lineHeight: 1.7,
                        }}
                    >
                        {activeMember?.data || "No biography available."}
                    </Typography>
                </Box>
            </Collapse>
        </Box>
    );
}